import Column from '../types/Column';
import { CHARTS_COLUMN } from '../types/ChartsColumn';
import { RanksHeaderItem } from '../atoms/Ranks';
import { RanksComparisonGroup } from '../molecules/RanksTooltip';
import Rank from '../types/Rank';
import Team from '../types/Team';
import { capitalizeFirstLetter } from './player';
import { StatsItem } from '../types/StatsPlayer';

export const PROFILE_STATS_CATEGORIES_ROWS_COUNT = 5;
export const PROFILE_STATS_CATEGORY = {
  HS_CLASS: 'hs-class',
  DIVISION_1: 'div-1',
  TEAM_SELECT_TEAM: 'team-select-team',
  TEAM_SELECT_CONFERENCE: 'team-select-conference',
  NFL_DRAFT: 'nfl-draft',
  TRANSFER_PORTAL: 'transfer-portal',
  TEAM_SELECT_STARTER: 'team-select-team-starters',
  CONFERENCE_STARTER: 'team-select-conference-starters',
  ALL_CONFERENCE: 'all-conference',
  ALL_AMERICAN: 'all-american',
  TEAM_SELECT_TEAM_NFL: 'team-select-team-nfl',
  NFL_UDFA: 'nfl-udfa',
  PRO_BOWL: 'pro-bowl',
  ALL_PRO: 'all-pro',
} as const;

export type ProfileStatsCategory = typeof PROFILE_STATS_CATEGORY[keyof typeof PROFILE_STATS_CATEGORY];

export const PROFILE_STATS_CATEGORIES_NON_NFL_LOCKED: ProfileStatsCategory[] = [
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE,
];
export const PROFILE_STATS_CATEGORIES_NFL_LOCKED: ProfileStatsCategory[] = [
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM_NFL,
];

export const PROFILE_STATS_CATEGORIES_COLLEGE_DEFAULT: ProfileStatsCategory[] = [
  PROFILE_STATS_CATEGORY.HS_CLASS,
  PROFILE_STATS_CATEGORY.DIVISION_1,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE,
  PROFILE_STATS_CATEGORY.NFL_DRAFT,
];
export const PROFILE_STATS_CATEGORIES_NFL_DEFAULT: ProfileStatsCategory[] = [
  PROFILE_STATS_CATEGORY.DIVISION_1,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM_NFL,
  PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM,
  // PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE,
  PROFILE_STATS_CATEGORY.NFL_UDFA,
  PROFILE_STATS_CATEGORY.NFL_DRAFT,
];

export const PROFILE_STATS_CATEGORY_TO_COLUMN: Partial<Record<ProfileStatsCategory, CHARTS_COLUMN>> = {
  [PROFILE_STATS_CATEGORY.HS_CLASS]: CHARTS_COLUMN.HS_CLASS,
  [PROFILE_STATS_CATEGORY.DIVISION_1]: CHARTS_COLUMN.DIVISION,
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM]: CHARTS_COLUMN.TEAM_SELECT,
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE]: CHARTS_COLUMN.SEC,
  [PROFILE_STATS_CATEGORY.NFL_DRAFT]: CHARTS_COLUMN.NFL_DRAFT,
  [PROFILE_STATS_CATEGORY.TRANSFER_PORTAL]: CHARTS_COLUMN.TRANSFER_PORTAL,
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_STARTER]: CHARTS_COLUMN.TEAM_SELECT_STARTER,
  [PROFILE_STATS_CATEGORY.CONFERENCE_STARTER]: CHARTS_COLUMN.CONFERENCE_STARTER,
  [PROFILE_STATS_CATEGORY.ALL_CONFERENCE]: CHARTS_COLUMN.ALL_CONFERENCE,
  [PROFILE_STATS_CATEGORY.ALL_AMERICAN]: CHARTS_COLUMN.ALL_AMERICAN,
  // [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM_NFL]: CHARTS_COLUMN.TEAM_SELECT_NFL,
  // [PROFILE_STATS_CATEGORY.NFL_UDFA]: CHARTS_COLUMN.NFL_UDFA,
  // [PROFILE_STATS_CATEGORY.PRO_BOWL]: CHARTS_COLUMN.PRO_BOWL,
  // [PROFILE_STATS_CATEGORY.ALL_PRO]: CHARTS_COLUMN.ALL_PRO,
};

export const PROFILE_STATS_CATEGORY_TO_COLUMN_NFL: Partial<Record<ProfileStatsCategory, CHARTS_COLUMN>> = {
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM]: CHARTS_COLUMN.TEAM_SELECT,
  // [PROFILE_STATS_CATEGORY.DIVISION_1]: CHARTS_COLUMN.DIVISION,
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM_NFL]: CHARTS_COLUMN.TEAM_SELECT_NFL,
  [PROFILE_STATS_CATEGORY.NFL_UDFA]: CHARTS_COLUMN.NFL_UDFA,
  [PROFILE_STATS_CATEGORY.NFL_DRAFT]: CHARTS_COLUMN.NFL_DRAFT,
  [PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE]: CHARTS_COLUMN.SEC,
  [PROFILE_STATS_CATEGORY.ALL_CONFERENCE]: CHARTS_COLUMN.ALL_CONFERENCE,
  [PROFILE_STATS_CATEGORY.ALL_AMERICAN]: CHARTS_COLUMN.ALL_AMERICAN,
  [PROFILE_STATS_CATEGORY.PRO_BOWL]: CHARTS_COLUMN.PRO_BOWL,
  [PROFILE_STATS_CATEGORY.ALL_PRO]: CHARTS_COLUMN.ALL_PRO,
};

export function getProfileStatsCategoryOptions (isNFL:boolean): ProfileStatsCategory[] {
  const defaultCategories = isNFL ? PROFILE_STATS_CATEGORIES_NFL_DEFAULT : PROFILE_STATS_CATEGORIES_COLLEGE_DEFAULT;
  const mappedCategories = Object.keys(isNFL ? PROFILE_STATS_CATEGORY_TO_COLUMN_NFL : PROFILE_STATS_CATEGORY_TO_COLUMN) as ProfileStatsCategory[];

  return Array.from(new Set([...defaultCategories, ...mappedCategories]));
}

export function getNormalizedProfileStatsCategories (categories:string[] | null | undefined, isNFL:boolean) {
  const defaultCategories = isNFL ? PROFILE_STATS_CATEGORIES_NFL_DEFAULT : PROFILE_STATS_CATEGORIES_COLLEGE_DEFAULT;
  const availableCategories = getProfileStatsCategoryOptions(isNFL);
  const categoriesToUse = Array.isArray(categories) && categories.length ? categories : defaultCategories;
  const knownCategories = categoriesToUse.filter((category:string) => availableCategories.includes(category as ProfileStatsCategory)) as ProfileStatsCategory[];
  const uniqueCategories = Array.from(new Set(knownCategories));

  if (!isNFL) {
    PROFILE_STATS_CATEGORIES_NON_NFL_LOCKED.forEach((category:ProfileStatsCategory) => {
      if (!uniqueCategories.includes(category)) {
        uniqueCategories.push(category);
      }
    });
  }

  const fillPool = [...defaultCategories, ...availableCategories];
  const missingCategories = fillPool.filter((category:ProfileStatsCategory) => !uniqueCategories.includes(category));

  return [...uniqueCategories, ...missingCategories].slice(0, PROFILE_STATS_CATEGORIES_ROWS_COUNT);
}

export function categoriesToColumns (categories:string[], isNFL?:boolean) {
  return categories
    .map((category:string) => isNFL ? PROFILE_STATS_CATEGORY_TO_COLUMN_NFL[category as ProfileStatsCategory] : PROFILE_STATS_CATEGORY_TO_COLUMN[category as ProfileStatsCategory])
    .filter((columnName): columnName is CHARTS_COLUMN => Boolean(columnName))
    .map((columnName:CHARTS_COLUMN) => ({ label: capitalizeFirstLetter(columnName)  ,name: columnName, selected: true } as Column));
}

interface ProfileStatsHeaderOptions {
  recruitingClass?: number | string;
  collegeTeam?: Team;
  nflTeam?: Team;
}

export function getCollegeHeaderItemByColumn (column:Column, options:ProfileStatsHeaderOptions): RanksHeaderItem {
  const { collegeTeam } = options;

  switch (column.name) {
    case CHARTS_COLUMN.HS_CLASS:
      return { title: 'HS Class'};
    case CHARTS_COLUMN.DIVISION:
      return { title: 'Division-I'};
    case CHARTS_COLUMN.TEAM_SELECT:
      return { logo: collegeTeam?.logo247 || collegeTeam?.logoESPN || collegeTeam?.logoAlt };
    case CHARTS_COLUMN.SEC:
      return { logo: collegeTeam?.conference?.iconUrl || '' };
    case CHARTS_COLUMN.NFL_DRAFT:
      return { logo: '/images/nfl-draft.png' };
    case CHARTS_COLUMN.TRANSFER_PORTAL:
      return { title: 'Transfer Portal' };
    case CHARTS_COLUMN.TEAM_SELECT_STARTER:
      return { title: 'Team Starter' };
    case CHARTS_COLUMN.CONFERENCE_STARTER:
      return { title: 'Conference Starter' };
    case CHARTS_COLUMN.ALL_CONFERENCE:
      return { title: 'All-Conference' };
    case CHARTS_COLUMN.ALL_AMERICAN:
      return { title: 'All-American' };
    default:
      return { logo: '/images/nfl-draft.png' };
  }
}

export function getNflHeaderItemByColumn (column:Column, options:ProfileStatsHeaderOptions): RanksHeaderItem {
  const { collegeTeam, nflTeam } = options;

  switch (column.name) {
    case CHARTS_COLUMN.TEAM_SELECT:
      return { logo: collegeTeam?.logo247 || collegeTeam?.logoESPN || collegeTeam?.logoAlt };
    case CHARTS_COLUMN.SEC:
      return { logo: collegeTeam?.conference?.iconUrl || '' };
    case CHARTS_COLUMN.TEAM_SELECT_NFL:
      return { logo: nflTeam?.logo247 || nflTeam?.logoESPN || nflTeam?.logoAlt };
    case CHARTS_COLUMN.NFL_UDFA:
      return { title: 'NFL', subtitle: 'UDFA' };
    case CHARTS_COLUMN.NFL_DRAFT:
      return { logo: '/images/nfl-draft.png' };
    case CHARTS_COLUMN.ALL_CONFERENCE:
      return { title: 'All-Conference' };
    case CHARTS_COLUMN.ALL_AMERICAN:
      return { title: 'All-American' };
    case CHARTS_COLUMN.PRO_BOWL:
      return { title: 'Pro Bowl' };
    case CHARTS_COLUMN.ALL_PRO:
      return { title: 'All-Pro' };
    default:
      return { logo: '/images/nfl-draft.png' };
  }
}

interface ProfileStatsComparisonRankValueOptions {
  column: Column;
  columnIndex: number;
  comparisonStats?: StatsItem[];
  isNFL?: boolean;
  isHSSports?: boolean;
  newProfileEnabled?: boolean;
}

function getCategoryByColumnName(columnName: string, isNFL: boolean): ProfileStatsCategory | undefined {
  const categoryToColumnMap = isNFL ? PROFILE_STATS_CATEGORY_TO_COLUMN_NFL : PROFILE_STATS_CATEGORY_TO_COLUMN;
  const mappingEntry = (Object.entries(categoryToColumnMap) as [ProfileStatsCategory, CHARTS_COLUMN][])
    .find(([, mappedColumnName]) => mappedColumnName === columnName);

  return mappingEntry?.[0];
}

function getDefaultComparisonGroupByIndex(columnName: string, isNFL: boolean, playerCount?: number): string | undefined {
  const category = getCategoryByColumnName(columnName, isNFL);
  if (playerCount && Number(playerCount) > 1000) {
    return undefined;
  } else {
    return category;
  }

  // if (!category) {
  //   return undefined;
  // }

  // if (isNFL) {
  //   if (
  //     category === PROFILE_STATS_CATEGORY.TEAM_SELECT_TEAM
  //     || category === PROFILE_STATS_CATEGORY.TEAM_SELECT_CONFERENCE
  //   ) {
  //     return undefined;
  //   }

  //   return category;
  // }

  // if (
  //   category === PROFILE_STATS_CATEGORY.HS_CLASS
  //   || category === PROFILE_STATS_CATEGORY.DIVISION_1
  // ) {
  //   return undefined;
  // }

  // return category;
}

export function getProfileStatsComparisonRankValue (options:ProfileStatsComparisonRankValueOptions): Rank {
  const {
    column,
    columnIndex,
    comparisonStats,
    isNFL = false,
    isHSSports = false,
    newProfileEnabled = false,
  } = options;

  const stat = comparisonStats?.[columnIndex];
  const baseValue: Rank = {
    percentile: Math.round(Number(stat?.percentile || 0)),
    value: isHSSports ? 'Participation' : `${Number(stat?.playerRank || 0)}/${Number(stat?.playerCount || 1)}`,
  };

  if (isHSSports) {
    return baseValue;
  }

  if (newProfileEnabled) {
    const comparisonGroup = getDefaultComparisonGroupByIndex(column.name, isNFL, stat?.playerCount || 0);
    return comparisonGroup
      ? { ...baseValue, comparisonGroup }
      : baseValue;
  }

  if (isNFL) {
    if (column.name === CHARTS_COLUMN.TEAM_SELECT || column.name === CHARTS_COLUMN.TEAM_SELECT_NFL) {
      return { ...baseValue, comparisonGroup: RanksComparisonGroup.NFL_TEAM };
    }

    if (column.name === CHARTS_COLUMN.SEC || column.name === CHARTS_COLUMN.NFL_UDFA) {
      return { ...baseValue, comparisonGroup: RanksComparisonGroup.NFL_UDFA };
    }

    if (column.name === CHARTS_COLUMN.NFL_DRAFT) {
      return { ...baseValue, comparisonGroup: RanksComparisonGroup.NFL_DRAFT };
    }

    return baseValue;
  }

  if (column.name === CHARTS_COLUMN.TEAM_SELECT) {
    return { ...baseValue, comparisonGroup: RanksComparisonGroup.COLLEGE_TEAM };
  }

  if (column.name === CHARTS_COLUMN.SEC) {
    return { ...baseValue, comparisonGroup: RanksComparisonGroup.COLLEGE_CONFERENCE };
  }

  if (column.name === CHARTS_COLUMN.NFL_DRAFT) {
    return { ...baseValue, comparisonGroup: RanksComparisonGroup.NFL_DRAFT };
  }

  return baseValue;
}

export const DEFAULT_PROFILE_STATS_COLUMNS = categoriesToColumns(PROFILE_STATS_CATEGORIES_COLLEGE_DEFAULT);
